import { useSelector } from 'react-redux';
import React, { useState, useEffect } from 'react';

import { Box, Grid, LinearProgress, CircularProgress } from '@mui/material';

import { billingStyles } from './style/style';
import { Typography, Button } from '../../shared';
import { useCompanyService } from 'store/services/company.service';

const classes = billingStyles({});

const PlanCard = ({ title, plan, price, features, usage, onUpgrade }) => {
    return (
        <Box sx={classes.planCard}>
            <Box sx={classes.planCardHeader}>
                <Box>
                    <Typography text={title} customStyles={classes.planTitle} />
                    <Typography text={plan} customStyles={classes.planSubtitle} />
                </Box>
                <Typography text={price} customStyles={classes.planPrice} />
            </Box>

            <Box>
                {features.map((feature, index) => (
                    <Box key={index} sx={classes.featureItem}>
                        <span>•</span>
                        <Typography text={feature} customStyles={classes.featureText} />
                    </Box>
                ))}
            </Box>

            {usage && (
                <Box sx={classes.usageContainer}>
                    <Box sx={classes.usageHeader}>
                        <Typography
                            text={`${usage.used} of ${usage.total} used`}
                            customStyles={classes.usageText}
                        />
                        <Typography
                            text={`${usage.percentage}%`}
                            customStyles={classes.usageText}
                        />
                    </Box>
                    <LinearProgress
                        variant="determinate"
                        value={usage.percentage}
                        sx={classes.progressBar}
                    />
                </Box>
            )}

            <Button
                text="Upgrade Plan"
                variant="contained"
                fullWidth
                customStyles={classes.upgradeButton}
                onClick={onUpgrade}
            />
        </Box>
    );
};

const Billing = () => {
    const { data } = useSelector((state) => state.company);
    const { getCompanyBilling, createStripeSession } = useCompanyService();
    const [billingData, setBillingData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const handleCreateStripeSession = async () => {
        const response = await createStripeSession(data?.id);
        if (response?.url) {
            window.location.href = response?.url;
        }
    };


    useEffect(() => {
        const fetchBillingData = async () => {
            try {
                if (data?.id) {
                    const response = await getCompanyBilling(data.id);
                    setBillingData(response.data);
                    setLoading(false);
                }
            } catch (err) {
                setError('No billing data available');
                setLoading(false);
            }
        };
    
        fetchBillingData();
    }, [data?.id]);
    
    

    const formatNumber = (num) => {
        return new Intl.NumberFormat().format(num);
    };

    const calculatePercentage = (used, total) => {
        if (total === null || total === 0) return 0;
        return Math.min(Math.round((used / total) * 100), 100);
    };

   

    if (loading) {
        return (
            <Box
                sx={{
                    ...classes.billing,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '300px',
                }}
            >
                <CircularProgress size={60} thickness={4} />
                <Typography
                    text="Loading billing information..."
                    customStyles={{
                        marginTop: 3,
                        color: 'text.secondary',
                    }}
                />
            </Box>
        );
    }

    if (error || !billingData) {
        return (
            <Box sx={classes.billing}>
                <Typography
                    text={error || 'No billing data available'}
                    customStyles={{ color: 'error.main', textAlign: 'center' }}
                />
            </Box>
        );
    }

    const { email_used, email_limit, purchase_volume_used, purchase_volume_limit } = billingData;
    const emailPercentage = calculatePercentage(email_used, email_limit);
    const volumePercentage = calculatePercentage(purchase_volume_used, purchase_volume_limit);

    return (
        <Box sx={classes.billing}>
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Typography text="Billing & Usage" customStyles={classes.mainHeading} />
                    <Typography
                        text="View your current usage and limits"
                        customStyles={classes.subHeading}
                    />
                </Grid>

                <Grid item xs={12} md={12}>
                    <PlanCard
                        title="Email Usage"
                        plan={`${formatNumber(email_used)} / ${formatNumber(
                            email_limit,
                        )} emails used`}
                        price={`${emailPercentage}% of limit used`}
                        features={[]}
                        usage={{
                            used: formatNumber(email_used),
                            total: formatNumber(email_limit),
                            percentage: emailPercentage,
                        }}
                        onUpgrade={handleCreateStripeSession}
                    />
                </Grid>
                <Grid item xs={12} md={12}>
                    <PlanCard
                        title="Purchase Volume"
                        plan={purchase_volume_limit === null 
                            ? `${formatNumber(purchase_volume_used)} used (Unlimited)`
                            : `${formatNumber(purchase_volume_used)} / ${formatNumber(purchase_volume_limit)} used`
                        }
                        price={purchase_volume_limit === null 
                            ? "Unlimited plan"
                            : `${volumePercentage}% of limit used`
                        }
                        features={[]}
                        usage={purchase_volume_limit === null ? null : {
                            used: formatNumber(purchase_volume_used),
                            total: formatNumber(purchase_volume_limit),
                            percentage: volumePercentage,
                        }}
                        onUpgrade={handleCreateStripeSession}
                    />
                </Grid>
            </Grid>
        </Box>
    );
};

export default Billing;
